import { useEffect, useId, useRef, type ReactNode } from 'react'
import Icon from '../docs/Icon'

type DialogProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** `sheet` slides in from the right; `dialog` is centered. */
  variant?: 'sheet' | 'dialog'
  children: ReactNode
  className?: string
  /** Controls in the head, beside the close button. */
  actions?: ReactNode
}

/**
 * A modal built on the native <dialog>: the browser supplies the focus trap,
 * Escape to close, inert background and focus return to the opener.
 */
export default function Dialog({ open, onClose, title, variant = 'dialog', children, className, actions }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
      // The browser focuses the first control; a field marked
      // data-autofocus (a search box, say) takes focus instead.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      className={`mds-${variant}${className ? ` ${className}` : ''}`}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
    >
      {open && (
        <div className={`mds-${variant}__inner`}>
          <div className={`mds-${variant}__head`}>
            <h2 id={titleId} className="mds-h3">
              {title}
            </h2>
            <span className="mds-dialog__actions">
              {actions}
              <button type="button" className="mds-icon-button" aria-label="Close" onClick={onClose}>
                <Icon name="close" />
              </button>
            </span>
          </div>
          {children}
        </div>
      )}
    </dialog>
  )
}
